/* eslint-disable no-bitwise -- a 32-bit hash and PRNG are bit arithmetic by definition */
/**
 * The two single-beat charts drawn next to each cluster's overlay
 * (project_management#561).
 *
 * They used to be picked with `Math.random()`: every render of the same report
 * — the browser view, the PDF, a regenerated PDF, even a re-render of the same
 * page — showed two different beats, so no two copies of a report matched.
 * The pick is now deterministic, seeded by the analysis, the beat group and
 * the cluster: the same analysis always shows the same two beats.
 *
 * The rule is otherwise unchanged: two distinct indexes in
 * `[0, min(max, 100))`, or `[0, 0]` when `max < 2`.
 */

/** 32-bit FNV-1a: a stable seed from a string. */
export function hashSeed(text: string): number {
  let hash = 0x811c9dc5;

  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return hash;
}

/** mulberry32 — a small, well-distributed PRNG returning [0, 1). */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;

    let t = state;

    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function clusterPickSeed(analysisId: string, annotationValue: string, clusterNumber: number) {
  return hashSeed(`${analysisId}:${annotationValue}:${clusterNumber}`);
}

/** Two distinct excerpt indexes below `min(max, 100)`; `[0, 0]` when `max < 2`. */
export function pickTwoExcerptIndexes(max: number, seed: number): [number, number] {
  if (max < 2) return [0, 0];

  const random = seededRandom(seed);
  const maxNumber = max > 100 ? 100 : max;

  const first = Math.floor(random() * maxNumber);
  let second = Math.floor(random() * maxNumber);

  while (first === second) {
    second = Math.floor(random() * maxNumber);
  }

  return [first, second];
}
